import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useCesium } from '@/context/CesiumContext';
import { assetPositionFlyOptions } from '@/lib/utils/homeView';
import { getAssetOptions } from '@/queries/assets/get-asset';

/**
 * Flies the map to a deleted asset previewed read-only (GH #331): its home view or coordinates, and
 * nothing else. Its models, damages and sensors went with it, so unlike InitialZoomController this
 * loads only the deleted asset itself.
 */
export function AssetPositionZoom({ assetId }: { assetId: string }) {
	const { viewer, setHomeView } = useCesium();
	const hasZoomed = useRef(false);
	const { data: asset, isLoading } = useQuery({
		...getAssetOptions(assetId, true, { deleted: true }),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (!viewer || isLoading || hasZoomed.current) return;
		hasZoomed.current = true;
		const position = asset ? assetPositionFlyOptions(asset) : null;
		if (!position) {
			viewer.camera.flyHome(2.0);
			return;
		}
		const flyOptions = { ...position, duration: 2.0 };
		setHomeView({ type: 'fly', options: flyOptions });
		viewer.camera.flyTo(flyOptions);
	}, [viewer, isLoading, asset, setHomeView]);

	return null;
}
